import { getRumClient } from '../../lib/rum/rumClient';
import { isNetworkError, logRumError } from '../../lib/rum/rumErrorLogger';
import { getRumFeatureErrorMessage, RumFeature } from '../../lib/rum/rumFeatures';
import { AutoApplySetupAction, type AutoApplySetupReviewAction } from './autoApplySetupActions';

type ResumeSqPrefillAction =
    | typeof AutoApplySetupAction.Continue
    | typeof AutoApplySetupAction.SaveSqPrefill;

export interface AutoApplySetupRumErrorContext {
    page: string;
    action: string;
    operation?: string;
    failureKind?: 'verification';
}

export const AutoApplySetupRumContext = {
    EntryModeLookup: { page: 'index', action: AutoApplySetupAction.LookupEntryMode },
    AutomationLevelLoad: { page: 'automation-level', action: AutoApplySetupAction.Load },
    AutomationLevelSave: {
        page: 'automation-level',
        action: AutoApplySetupAction.Continue,
        operation: 'save',
    },
    AutomationLevelItv: {
        page: 'automation-level',
        action: AutoApplySetupAction.Continue,
        operation: 'save',
        failureKind: 'verification',
    },
    MatchPreferencesLoad: { page: 'match-preferences', action: AutoApplySetupAction.Load },
    PastAnswersLoad: { page: 'past-answers', action: AutoApplySetupAction.Load },
    PastAnswersLoadMore: { page: 'past-answers', action: AutoApplySetupAction.LoadMore },
    PastAnswersAction: {
        page: 'past-answers',
        action: AutoApplySetupAction.UpdatePreference,
    },
    LegalDisclaimerLoad: { page: 'legal-disclaimer', action: AutoApplySetupAction.Load },
    LegalDisclaimerSubmit: {
        page: 'legal-disclaimer',
        action: AutoApplySetupAction.Activate,
        operation: 'submit',
    },
    LegalDisclaimerItv: {
        page: 'legal-disclaimer',
        action: AutoApplySetupAction.Activate,
        operation: 'submit',
        failureKind: 'verification',
    },
    ResumeLoad: { page: 'resume', action: AutoApplySetupAction.Load },
    ResumeSave: {
        page: 'resume',
        action: AutoApplySetupAction.Continue,
        operation: 'save',
    },
    ResumeSqPrefillSave: (action: ResumeSqPrefillAction) => ({
        page: 'resume',
        action,
        operation: 'save-sq-prefill',
    }),
    ResumeItv: {
        page: 'resume',
        action: AutoApplySetupAction.Continue,
        operation: 'save',
        failureKind: 'verification',
    },
    ResumeUpload: {
        page: 'resume',
        action: AutoApplySetupAction.UploadFileResume,
        operation: 'upload',
    },
    ReviewLoad: { page: 'review', action: AutoApplySetupAction.Load },
    ReviewPreferenceSave: (questionType: 'sq' | 'dq') => ({
        page: 'review',
        action:
            questionType === 'sq'
                ? AutoApplySetupAction.UpdateSqPreference
                : AutoApplySetupAction.UpdateDqPreference,
        operation: questionType === 'sq' ? 'save-sq-prefill' : 'save-dq-consent',
    }),
    ReviewSubmit: (action: AutoApplySetupReviewAction) => ({
        page: 'review',
        action,
        operation: 'submit',
    }),
    ReviewItv: (action: AutoApplySetupReviewAction) => ({
        page: 'review',
        action,
        operation: 'submit',
        failureKind: 'verification' as const,
    }),
    ReviewDqConsentSave: (action: AutoApplySetupReviewAction) => ({
        page: 'review',
        action,
        operation: 'save-dq-consent',
    }),
    ReviewDqConsentItv: (action: AutoApplySetupReviewAction) => ({
        page: 'review',
        action,
        operation: 'save-dq-consent',
        failureKind: 'verification' as const,
    }),
} as const;

/**
 * Reports a sanitized copy of a setup error with stable journey metadata. Browser network failures
 * do not fail the view unless the caller explicitly overrides their classification.
 */
export const logAutoApplySetupRumError = (
    error: unknown,
    context: AutoApplySetupRumErrorContext,
    isPageFailure?: boolean,
): void => {
    const rumContext = {
        feature: RumFeature.AutoApplySetup,
        ...context,
    };
    logRumError(getRumClient(), error, {
        isPageFailure: isPageFailure ?? !isNetworkError(error),
        context: rumContext,
        sanitizedMessage: getRumFeatureErrorMessage(rumContext),
    });
};
